import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CommandBar from "@/components/CommandBar";
import Assistant from "@/components/Assistant";
import TimerWidget from "@/components/TimerWidget";
import NewPublicationModal from "@/components/NewPublicationModal";
import NewClientModal from "@/components/NewClientModal";
import RdvModal from "@/components/RdvModal";
import AppSidebar from "@/components/layout/AppSidebar";
import AppHeader from "@/components/layout/AppHeader";

/* Layout global DGA — Phase B/C : AppSidebar (bleu nuit) + AppHeader (recherche
   globale, notifications, paramètres, profil). Toute la logique existante est
   conservée : branding personnalisable, compteurs, raccourci ⌘K, modales de
   création, minuteur, assistant, événements temps réel (sf:*). */
export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [unread, setUnread] = useState(0);
  const [rdvCount, setRdvCount] = useState(0);
  const [demandesCount, setDemandesCount] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [pubOpen, setPubOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [rdvOpen, setRdvOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brand, setBrand] = useState({ app_name: "DGA", app_logo_url: "", app_favicon_url: "", brand_primary_color: "#0B132B", brand_secondary_color: "#a89b8c" });

  useEffect(() => {
    const loadBrand = () => api.get("/settings")
      .then((r) => setBrand({
        app_name: r.data.app_name || "DGA", app_logo_url: r.data.app_logo_url || "",
        app_favicon_url: r.data.app_favicon_url || "",
        brand_primary_color: r.data.brand_primary_color || "#0B132B",
        brand_secondary_color: r.data.brand_secondary_color || "#a89b8c",
      }))
      .catch(() => {});
    loadBrand();
    window.addEventListener("sf:brand", loadBrand);
    return () => window.removeEventListener("sf:brand", loadBrand);
  }, []);

  useEffect(() => {
    const link = document.querySelector('link[rel="icon"]');
    if (link && brand.app_favicon_url) link.href = brand.app_favicon_url;
    document.documentElement.style.setProperty("--brand", brand.brand_primary_color || "#0B132B");
    document.documentElement.style.setProperty("--brand-2", brand.brand_secondary_color || "#a89b8c");
  }, [brand]);

  const loadClients = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => {
    loadClients();
    const loadUnread = () =>
      api.get("/notifications").then((r) => {
        const items = r.data.filter((n) => !n.read && !n.archived);
        setUnread(items.length);
        setRdvCount(items.filter((n) => n.type === "rdv").length);
      }).catch(() => {});
    const loadDemandes = () =>
      api.get("/client-requests/attention-count").then((r) => setDemandesCount(r.data.count)).catch(() => {});
    loadUnread();
    loadDemandes();
    const t = setInterval(() => { loadUnread(); loadDemandes(); }, 30000);
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen(true); } };
    window.addEventListener("keydown", onKey);
    window.addEventListener("focus", loadClients);
    const onRefresh = () => loadClients();
    window.addEventListener("sf:clients", onRefresh);
    window.addEventListener("sf:demandes", loadDemandes);
    window.addEventListener("sf:notifs", loadUnread);
    return () => {
      clearInterval(t);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("focus", loadClients);
      window.removeEventListener("sf:clients", onRefresh);
      window.removeEventListener("sf:demandes", loadDemandes);
      window.removeEventListener("sf:notifs", loadUnread);
    };
  }, []);

  const counts = { rdv: rdvCount, demandes: demandesCount };
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Sidebar desktop — bleu nuit fixe */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30">
        <AppSidebar brand={brand} counts={counts} onQuickRdv={() => setRdvOpen(true)} onNavigate={() => {}} />
      </aside>

      {/* Sidebar mobile — drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-[#0B132B]/50 backdrop-blur-sm" onClick={closeMobile} />
          <aside className="absolute inset-y-0 left-0 w-72 shadow-xl animate-fade-up">
            <AppSidebar brand={brand} counts={counts} onQuickRdv={() => { setRdvOpen(true); closeMobile(); }} onNavigate={closeMobile} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <AppHeader
          brand={brand} user={user} unread={unread}
          onSearch={() => setCmdOpen(true)}
          onOpenMobile={() => setMobileOpen(!mobileOpen)} mobileOpen={mobileOpen}
          onLogout={async () => { await logout(); navigate("/login"); }}
          onNewPub={() => setPubOpen(true)} onNewClient={() => setClientOpen(true)}
        />
        <main className="p-4 lg:p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>

      {/* Widgets et modales globaux — inchangés */}
      <TimerWidget />
      <CommandBar open={cmdOpen} setOpen={setCmdOpen} />
      <Assistant />
      <NewPublicationModal open={pubOpen} setOpen={setPubOpen} clients={clients} />
      <NewClientModal open={clientOpen} setOpen={setClientOpen} onCreated={(c) => { loadClients(); navigate(`/clients/${c.id}`); }} />
      {rdvOpen && <RdvModal rdv={null} onClose={() => setRdvOpen(false)} onSaved={() => navigate("/rdv")} />}
    </div>
  );
}
